'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { WHATSAPP_CONTACTS, openWhatsAppChat, createServiceWhatsAppMessage } from '@/lib/whatsappContacts';
import { trackEvent } from '@/lib/analytics';

interface WhatsAppContactButtonProps {
  variant?: 'default' | 'floating' | 'inline' | 'cta';
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  serviceName?: string;
}

export default function WhatsAppContactButton({
  variant = 'default',
  className = '',
  size = 'md',
  showLabel = true,
  serviceName
}: WhatsAppContactButtonProps) {
  const [showModal, setShowModal] = useState(false);

  const handleContactSelect = (number: string, defaultMessage: string) => {
    const message = serviceName ? createServiceWhatsAppMessage(serviceName) : defaultMessage;
    
    // Track WhatsApp click
    trackEvent('whatsapp_click', {
      event_category: 'Contact',
      event_label: serviceName || 'General Inquiry',
      contact_method: 'WhatsApp'
    });
    
    openWhatsAppChat(number, message);
    setShowModal(false);
  };

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  };

  const variantClasses = {
    default: 'bg-success text-success-foreground hover:shadow-md',
    floating: 'bg-success text-success-foreground hover:shadow-xl hover:scale-110 rounded-full',
    inline: 'bg-success text-success-foreground hover:shadow-md',
    cta: 'bg-success text-success-foreground hover:shadow-lg'
  };

  const buttonClasses = `
    flex items-center gap-2 rounded-lg transition-all duration-300 font-semibold
    ${variantClasses[variant]} ${sizeClasses[size]} ${className}
  `.trim();

  return (
    <>
      {/* WhatsApp Department Selector Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-text-primary/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full border border-border animate-slideUp">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-text-primary flex items-center gap-2">
                  <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" className="text-success" />
                  Choose Department
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-surface rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <Icon name="XMarkIcon" size={20} variant="outline" className="text-text-secondary" />
                </button>
              </div>
              <p className="text-sm text-text-secondary mt-2">
                Select which department you'd like to contact via WhatsApp
              </p>
            </div>
            
            <div className="p-6 space-y-3">
              {WHATSAPP_CONTACTS.map((contact, index) => (
                <button
                  key={index}
                  onClick={() => handleContactSelect(contact.number, contact.message)}
                  className="w-full block p-4 bg-surface hover:bg-primary/5 border-2 border-border hover:border-primary rounded-xl transition-all duration-300 group text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-success/10 text-success rounded-lg group-hover:bg-success group-hover:text-success-foreground transition-colors">
                      <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-text-primary group-hover:text-primary transition-colors">
                        {contact.label}
                      </div>
                      <div className="text-xs text-text-secondary mt-1">
                        {contact.description}
                      </div>
                    </div>
                    <Icon 
                      name="ArrowTopRightOnSquareIcon" 
                      size={20} 
                      variant="outline" 
                      className="text-text-secondary group-hover:text-primary transition-colors"
                    />
                  </div>
                </button>
              ))}
            </div>

            <div className="p-6 pt-0">
              {/* Info box removed */}
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Button */}
      <button
        onClick={() => setShowModal(true)}
        className={buttonClasses}
        aria-label="Contact us via WhatsApp"
      >
        <Icon 
          name="ChatBubbleLeftRightIcon" 
          size={size === 'sm' ? 18 : size === 'lg' ? 24 : 20} 
          variant="solid" 
        />
        {showLabel && <span>Chat on WhatsApp</span>}
      </button>
    </>
  );
}